import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { appClient } from "@/api/appClient";

const AuthContext = createContext(null);

async function identifyObservabilityUser(user) {
  const [analytics, monitoring] = await Promise.all([
    import("@/lib/analytics"),
    import("@/lib/monitoring"),
  ]);

  analytics.identifyUser(user);
  monitoring.setSentryUser(user);
}

async function clearObservabilityUser() {
  const [analytics, monitoring] = await Promise.all([
    import("@/lib/analytics"),
    import("@/lib/monitoring"),
  ]);

  analytics.resetAnalyticsUser();
  monitoring.clearSentryUser();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const previousUserEmailRef = useRef("");

  useEffect(() => {
    let isMounted = true;

    const refreshUser = async () => {
      const nextUser = await appClient.auth.me();
      if (!isMounted) return;

      setUser(nextUser || null);
      setIsLoadingAuth(false);

      if (nextUser?.email) {
        previousUserEmailRef.current = nextUser.email;
        identifyObservabilityUser(nextUser);
      } else if (previousUserEmailRef.current) {
        previousUserEmailRef.current = "";
        clearObservabilityUser();
      }
    };

    refreshUser();
    const unsubscribe = appClient.auth.subscribe(refreshUser);

    return () => {
      isMounted = false;
      unsubscribe?.();
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user?.email),
      isLoadingAuth,
      isLoadingPublicSettings: false,
      authError: null,
      appPublicSettings: {
        runtime_mode: appClient.runtime.mode,
      },
      logout: (shouldRedirect = true) =>
        appClient.auth.logout(shouldRedirect ? "/welcome" : undefined),
      navigateToLogin: (returnTo = window.location.href) =>
        appClient.auth.redirectToLogin(returnTo),
      checkAppState: async () => {
        const nextUser = await appClient.auth.me();
        setUser(nextUser || null);
        return nextUser;
      },
    }),
    [user, isLoadingAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

